import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Device, SettingField } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { SETTING_FIELD_TYPE, SETTING_FIELD_TYPE_OPTIONS } from '@/constants/treatmentLibrary';
import { DURATION_UNITS } from '@/utils/durationFormatting';
import {
  useRemoveDeviceImageMutation,
  useSaveDeviceMutation,
  useUploadDeviceImageMutation,
} from '@/hooks/useTreatmentLibraryMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { EntityImageUpload } from './EntityImageUpload';

interface OptionRow {
  labelEn: string;
  labelPl: string;
}

interface FieldRow {
  nameEn: string;
  namePl: string;
  unit: string;
  fieldType: string;
  options: OptionRow[];
}

interface DeviceFormModalProps {
  device: Device | null;
  onClose: () => void;
}

function toFieldRows(fields: SettingField[]): FieldRow[] {
  return fields.map(function toRow(field) {
    return {
      nameEn: field.nameEn,
      namePl: field.namePl,
      unit: field.unit,
      fieldType: field.fieldType,
      options: field.options.map(function toOptionRow(option) {
        return { labelEn: option.labelEn, labelPl: option.labelPl };
      }),
    };
  });
}

function emptyFieldRow(): FieldRow {
  return { nameEn: '', namePl: '', unit: '', fieldType: SETTING_FIELD_TYPE.number, options: [] };
}

export function DeviceFormModal({ device, onClose }: DeviceFormModalProps) {
  const { t } = useTranslation('treatmentLibrary');
  const isEditing = device !== null;
  const mutation = useSaveDeviceMutation();

  const [nameEn, setNameEn] = useState(device === null ? '' : device.nameEn);
  const [namePl, setNamePl] = useState(device === null ? '' : device.namePl);
  const [rows, setRows] = useState<FieldRow[]>(
    device === null ? [emptyFieldRow()] : toFieldRows(device.settingFields)
  );
  const [isActive, setIsActive] = useState(device === null ? true : device.isActive);
  const [imageUrl, setImageUrl] = useState<string | null>(device === null ? null : device.imageUrl);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImageRemoved, setPendingImageRemoved] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const uploadImage = useUploadDeviceImageMutation();
  const removeImage = useRemoveDeviceImageMutation();
  const isSaving = mutation.isPending || uploadImage.isPending || removeImage.isPending;

  const nameEnMissing = nameEn.trim().length === 0;
  const namePlMissing = namePl.trim().length === 0;

  function handleSelectImage(file: File) {
    setPendingImageFile(file);
    setPendingImageRemoved(false);
    setImageUrl(URL.createObjectURL(file));
  }

  function handleRemoveImage() {
    setPendingImageFile(null);
    setPendingImageRemoved(true);
    setImageUrl(null);
  }

  function updateRow(index: number, patch: Partial<FieldRow>) {
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index ? { ...row, ...patch } : row;
      });
    });
  }
  function removeRow(index: number) {
    setRows(function apply(previous) {
      return previous.filter(function keep(_row, rowIndex) {
        return rowIndex !== index;
      });
    });
  }
  function addRow() {
    setRows(function apply(previous) {
      return [...previous, emptyFieldRow()];
    });
  }
  function addOption(index: number, labelEn: string, labelPl: string) {
    const trimmedEn = labelEn.trim();
    const trimmedPl = labelPl.trim();
    if (trimmedEn.length === 0 || trimmedPl.length === 0) {
      return;
    }
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index
          ? { ...row, options: [...row.options, { labelEn: trimmedEn, labelPl: trimmedPl }] }
          : row;
      });
    });
  }
  function removeOption(index: number, optionIndex: number) {
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index
          ? {
              ...row,
              options: row.options.filter(function keep(_option, currentIndex) {
                return currentIndex !== optionIndex;
              }),
            }
          : row;
      });
    });
  }

  async function save() {
    if (nameEnMissing || namePlMissing) {
      setShowErrors(true);
      return;
    }
    const settingFields = rows
      .filter(function named(row) {
        return row.nameEn.trim().length > 0 && row.namePl.trim().length > 0;
      })
      .map(function toField(row) {
        return {
          nameEn: row.nameEn.trim(),
          namePl: row.namePl.trim(),
          unit: row.unit.trim(),
          fieldType: row.fieldType,
          options: row.fieldType === SETTING_FIELD_TYPE.choice ? row.options : [],
        };
      });
    try {
      const saved = await mutation.mutateAsync({
        deviceId: device === null ? null : device.id,
        payload: { nameEn: nameEn.trim(), namePl: namePl.trim(), settingFields, isActive },
      });
      if (pendingImageFile !== null) {
        await uploadImage.mutateAsync({ id: saved.id, file: pendingImageFile });
      } else if (pendingImageRemoved && device !== null && device.imageUrl !== null) {
        await removeImage.mutateAsync(saved.id);
      }
      showSuccessNotification(isEditing ? t('devices.updated') : t('devices.saved'));
      onClose();
    } catch (error) {
      showErrorNotification(resolveApiErrorMessage(error));
    }
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="bg-zoowhite w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-zooborder flex justify-between items-center bg-zoovpalelavender/40">
          <h3 className="text-lg font-bold text-zooindigo">
            {isEditing ? t('devices.editTitle') : t('devices.addTitle')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('form.cancel')}
            onClick={onClose}
            className="rounded-full"
          >
            <Icon name="X" className="text-zooindigo" />
          </Button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>
                {t('form.nameEn')} <span className="text-zooerrortext">*</span>
              </Label>
              <Input
                value={nameEn}
                hasError={showErrors && nameEnMissing}
                placeholder={t('devices.nameEnPlaceholder')}
                onChange={function update(changeEvent) {
                  setNameEn(changeEvent.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>
                {t('form.namePl')} <span className="text-zooerrortext">*</span>
              </Label>
              <Input
                value={namePl}
                hasError={showErrors && namePlMissing}
                placeholder={t('devices.namePlPlaceholder')}
                onChange={function update(changeEvent) {
                  setNamePl(changeEvent.target.value);
                }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('image.alt')}</Label>
            <EntityImageUpload
              imageUrl={imageUrl}
              onSelect={handleSelectImage}
              onRemove={handleRemoveImage}
              isBusy={isSaving}
            />
          </div>

          <div>
            <Label>{t('devices.settingsLabel')}</Label>
            <p className="text-xs text-zoomuted mb-2">{t('devices.settingsHint')}</p>
            <div className="border border-zooborder rounded-2xl divide-y divide-zooborder">
              {rows.map(function renderRow(row, index) {
                return (
                  <div key={index} className="p-3">
                    <div className="flex flex-col sm:grid sm:grid-cols-[1fr_1fr_0.7fr_0.9fr_auto] gap-2 sm:items-center">
                      <Input
                        className="py-2 text-[13px]"
                        value={row.nameEn}
                        placeholder={t('form.fieldNameEn')}
                        onChange={function update(changeEvent) {
                          updateRow(index, { nameEn: changeEvent.target.value });
                        }}
                      />
                      <Input
                        className="py-2 text-[13px]"
                        value={row.namePl}
                        placeholder={t('form.fieldNamePl')}
                        onChange={function update(changeEvent) {
                          updateRow(index, { namePl: changeEvent.target.value });
                        }}
                      />
                      {row.fieldType === SETTING_FIELD_TYPE.duration ? (
                        <SearchableSelect
                          inputId={`device-field-duration-unit-${index}`}
                          ariaLabel={t('form.unit')}
                          value={row.unit}
                          options={DURATION_UNITS.map(function toOption(unit) {
                            return { value: unit, label: unit };
                          })}
                          onChange={function update(nextValue) {
                            updateRow(index, { unit: nextValue });
                          }}
                        />
                      ) : (
                        <Input
                          className="py-2 text-[13px]"
                          value={row.unit}
                          placeholder={t('form.unit')}
                          onChange={function update(changeEvent) {
                            updateRow(index, { unit: changeEvent.target.value });
                          }}
                        />
                      )}
                      <SearchableSelect
                        inputId={`device-field-type-${index}`}
                        value={row.fieldType}
                        options={SETTING_FIELD_TYPE_OPTIONS.map(function toOption(fieldType) {
                          return { value: fieldType, label: t('form.type_' + fieldType) };
                        })}
                        onChange={function update(nextValue) {
                          updateRow(index, { fieldType: nextValue });
                        }}
                      />
                      <Button
                        variant="destructiveGhost"
                        size="icon"
                        aria-label={t('form.removeField')}
                        onClick={function remove() {
                          removeRow(index);
                        }}
                        className="w-8 h-8"
                      >
                        <Icon name="Trash" />
                      </Button>
                    </div>
                    {row.fieldType === SETTING_FIELD_TYPE.choice ? (
                      <OptionsEditor
                        options={row.options}
                        onAdd={function add(labelEn, labelPl) {
                          addOption(index, labelEn, labelPl);
                        }}
                        onRemove={function remove(optionIndex) {
                          removeOption(index, optionIndex);
                        }}
                      />
                    ) : null}
                  </div>
                );
              })}
              <div className="p-3">
                <Button variant="link" onClick={addRow} className="gap-2 text-[13px] font-bold">
                  <Icon name="Plus" /> {t('form.addField')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-zooborder flex items-center justify-between bg-zoooffwhite">
          <label className="flex items-center gap-2.5 text-[13px] font-semibold text-zooindigo">
            <ToggleSwitch
              isChecked={isActive}
              onToggle={function toggle() {
                setIsActive(function invert(previous) {
                  return previous === false;
                });
              }}
              accessibleLabel={t('form.activeLabel')}
            />
            {t('form.activeLabel')}
          </label>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('form.cancel')}
            </Button>
            <Button type="button" isLoading={isSaving} onClick={save}>
              {t('devices.save')}
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

interface OptionsEditorProps {
  options: OptionRow[];
  onAdd: (labelEn: string, labelPl: string) => void;
  onRemove: (optionIndex: number) => void;
}

function OptionsEditor({ options, onAdd, onRemove }: OptionsEditorProps) {
  const { t } = useTranslation('treatmentLibrary');
  const [draftEn, setDraftEn] = useState('');
  const [draftPl, setDraftPl] = useState('');

  function commit() {
    onAdd(draftEn, draftPl);
    setDraftEn('');
    setDraftPl('');
  }

  return (
    <div className="mt-2 pl-1 space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
        {t('devices.optionsLabel')}
      </div>
      {options.length === 0 ? (
        <p className="text-xs text-zoomuted">{t('devices.optionsEmpty')}</p>
      ) : (
        <div className="space-y-1.5">
          {options.map(function renderOption(option, optionIndex) {
            return (
              <div key={optionIndex} className="flex items-center gap-1.5">
                <span className="flex-1 min-w-0 truncate text-[12px] font-medium bg-zoovpalelavender text-zooprimary rounded-lg px-2 py-1">
                  {option.labelEn}
                </span>
                <span className="flex-1 min-w-0 truncate text-[12px] font-medium bg-zoovpalelavender text-zooprimary rounded-lg px-2 py-1">
                  {option.labelPl}
                </span>
                <button
                  type="button"
                  aria-label={t('devices.removeOption')}
                  onClick={function remove() {
                    onRemove(optionIndex);
                  }}
                  className="shrink-0 text-zoomuted hover:text-zooerrortext"
                >
                  <Icon name="X" className="text-[13px]" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          className="py-2 text-[13px]"
          value={draftEn}
          placeholder={t('devices.optionEnPlaceholder')}
          onChange={function update(changeEvent) {
            setDraftEn(changeEvent.target.value);
          }}
        />
        <Input
          className="py-2 text-[13px]"
          value={draftPl}
          placeholder={t('devices.optionPlPlaceholder')}
          onChange={function update(changeEvent) {
            setDraftPl(changeEvent.target.value);
          }}
          onKeyDown={function onKey(keyEvent) {
            if (keyEvent.key === 'Enter') {
              keyEvent.preventDefault();
              commit();
            }
          }}
        />
        <Button type="button" variant="outline" size="small" onClick={commit}>
          {t('devices.addOption')}
        </Button>
      </div>
    </div>
  );
}
